// How wide an actor's name needs its box to be, worked out without a browser
// (CLAUDE.md: one spacing rule for tiles and posters, site-wide).
//
// **A Call Sheet name is never cut** — it gets two lines, breaking between
// words — and on a small screen that is what decides how far apart the faces
// stand. The gap is sized per puzzle from its four names, **the unrevealed ones
// included**, so the tiles never shift when a face is revealed. The browser is
// never given an unrevealed name, so the width is worked out here, on the
// server, and only the one figure travels (`puzzles.name_em`, sent by
// `cs_play`); How to play's example, whose names are on the page anyway, works
// it out in the browser from the same table.
//
// **The table** is Inter at weight 500 — the face the names are set in — one
// advance width per character, in em, measured in a browser on 13 October 2026
// over every character in the people table (214 of them). Summing advances
// ignores kerning, which against the browser's own layout of all 376 names in
// the pool came out within 1.05px under and 10.8px over at 12px (over where a
// hyphen gives the browser a break this does not take). `SLACK` covers the
// under. A character not in the table counts as the widest Latin capital, so an
// unknown name errs towards a wider box, never a cut one.
const WIDTHS: Record<string, number> = {
  '0': 0.6455, '1': 0.415, '2': 0.6162, '3': 0.627, '4': 0.6563, '5': 0.603, '6': 0.6299, '7': 0.5713,
  '8': 0.6294, '\t': 0.2666, ' ': 0.2666, '!': 0.3042, '"': 0.4941, '$': 0.646, '&': 0.6533, "'": 0.313,
  '(': 0.3687, ')': 0.3687, ',': 0.3032, '-': 0.4624, '.': 0.3032, ':': 0.3032, ';': 0.3154, '@': 0.9824,
  'A': 0.709, 'B': 0.6567, 'C': 0.7334, 'D': 0.7217, 'E': 0.603, 'F': 0.5894, 'G': 0.7476, 'H': 0.7446,
  'I': 0.2725, 'J': 0.5752, 'K': 0.6875, 'L': 0.5654, 'M': 0.9126, 'N': 0.7563, 'O': 0.7666, 'P': 0.6416,
  'Q': 0.7686, 'R': 0.6479, 'S': 0.646, 'T': 0.6528, 'U': 0.7402, 'V': 0.709, 'W': 1.0029, 'X': 0.7007,
  'Y': 0.6963, 'Z': 0.6406, 'a': 0.5679, 'b': 0.6182, 'c': 0.5771, 'd': 0.6182, 'e': 0.5874, 'f': 0.3794,
  'g': 0.6196, 'h': 0.6016, 'i': 0.252, 'j': 0.252, 'k': 0.5591, 'l': 0.252, 'm': 0.8882, 'n': 0.6016,
  'o': 0.604, 'p': 0.6182, 'q': 0.6182, 'r': 0.3867, 's': 0.5386, 't': 0.3403, 'u': 0.6016, 'v': 0.5747,
  'w': 0.8291, 'x': 0.5571, 'y': 0.5752, 'z': 0.5591, '´': 0.3369, 'À': 0.709, 'Á': 0.709, 'Â': 0.709,
  'Å': 0.709, 'Ç': 0.7334, 'È': 0.603, 'É': 0.603, 'Í': 0.2725, 'Î': 0.2725, 'Ò': 0.7666, 'Ó': 0.7666,
  'Ô': 0.7666, 'Ö': 0.7666, 'Ø': 0.7666, 'Ú': 0.7402, 'Ü': 0.7402, 'Þ': 0.647, 'ß': 0.6299, 'à': 0.5679,
  'á': 0.5679, 'â': 0.5679, 'ã': 0.5679, 'ä': 0.5679, 'å': 0.5679, 'æ': 0.915, 'ç': 0.5771, 'è': 0.5874,
  'é': 0.5874, 'ê': 0.5874, 'ë': 0.5874, 'ì': 0.252, 'í': 0.252, 'î': 0.252, 'ï': 0.252, 'ð': 0.5879,
  'ñ': 0.6016, 'ò': 0.604, 'ó': 0.604, 'ô': 0.604, 'õ': 0.604, 'ö': 0.604, 'ø': 0.604, 'ù': 0.6016,
  'ú': 0.6016, 'û': 0.6016, 'ü': 0.6016, 'ý': 0.5752, 'þ': 0.6182, 'ÿ': 0.5752, 'ā': 0.522, 'ă': 0.522,
  'ą': 0.522, 'Ć': 0.6211, 'ć': 0.4702, 'Č': 0.6211, 'č': 0.4702, 'Ď': 0.7173, 'Đ': 0.7173, 'đ': 0.6084,
  'ē': 0.5313, 'ė': 0.5313, 'ę': 0.5313, 'ě': 0.5313, 'ğ': 0.6025, 'ī': 0.2612, 'İ': 0.2915, 'ı': 0.252,
  'Ľ': 0.4888, 'Ł': 0.4888, 'ł': 0.292, 'ń': 0.5835, 'ņ': 0.5835, 'ň': 0.5835, 'Ō': 0.7559, 'ō': 0.5972,
  'ő': 0.5972, 'œ': 0.9956, 'Ř': 0.6226, 'ř': 0.3701, 'Ś': 0.5444, 'ś': 0.4312, 'Ş': 0.5444, 'ş': 0.4312,
  'Š': 0.5444, 'š': 0.4312, 'ť': 0.4131, 'ū': 0.5835, 'ů': 0.5835, 'Ż': 0.5869, 'ż': 0.4644, 'Ž': 0.5869,
  'ž': 0.4644, 'ơ': 0.6118, 'ư': 0.624, 'Ș': 0.5444, 'ș': 0.4312, 'ț': 0.3613, 'ʻ': 0.2764, '̀': 0,
  '́': 0, 'А': 0.6714, 'Д': 0.73, 'М': 0.9243, 'Х': 0.6191, 'Э': 0.6196, 'и': 0.6021, 'л': 0.561,
  'н': 0.5928, 'о': 0.5972, 'с': 0.4702, 'у': 0.5083, 'э': 0.4697, 'я': 0.5322, 'Ṣ': 0.5444, 'ả': 0.522,
  'ẹ': 0.5313, 'ệ': 0.5313, 'ọ': 0.5972, 'ồ': 0.5972, 'ứ': 0.624, '​': 0, '‘': 0.2773, '’': 0.2773,
  '“': 0.4736, '”': 0.4707, '星': 1, '河': 1, '김': 1, '아': 1, '웅': 1, '원': 1, '유': 1, '임': 1,
  '진': 1, '철': 1, '청': 1, '﻿': 0,
  // A soft hyphen in a display name takes no width unless the word breaks there.
  '­': 0
};

/** The widest Latin capital, for anything not in the table. */
const UNKNOWN = WIDTHS['W']!;

/** 1.5px at 12px: what the measured worst case came in under the browser. */
const SLACK = 0.125;

const width = (text: string) => [...text].reduce((sum, ch) => sum + (WIDTHS[ch] ?? UNKNOWN), 0);

/**
 * The narrowest box, in em, that holds the name on two lines or fewer,
 * breaking only between words — a single word is its own width.
 */
export function nameEm(name: string): number {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return width(words[0] ?? '') + SLACK;
  let best = Infinity;
  for (let k = 1; k < words.length; k++) {
    best = Math.min(best, Math.max(width(words.slice(0, k).join(' ')), width(words.slice(k).join(' '))));
  }
  return best + SLACK;
}

/** A puzzle's figure: the widest of its four names, to three places. */
export function puzzleNameEm(names: string[]): number {
  return Math.round(Math.max(0, ...names.map(nameEm)) * 1000) / 1000;
}
